import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Loading, Card, Badge } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { IconArrowBack, IconPackage, IconEye } from '@tabler/icons-react'
import { useClient } from '@/stores/useClient.ts'
import { Order, OrderStatus } from '@/models/shop.ts'

export default function OrdersPage() {
  const navigate = useNavigate()
  const { driveId } = useParams<{ driveId: string }>()
  const client = useClient()
  const [orders, setOrders] = useState<Order[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchOrders()
  }, [])

  const fetchOrders = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await client.getOrders()
      setOrders(response.data)
    } catch (err) {
      console.error('Error fetching orders:', err)
      setError('Fehler beim Laden der Bestellungen')
    } finally {
      setIsLoading(false)
    }
  }

  const formatPrice = (price: string) => {
    return `€${parseFloat(price).toFixed(2)}`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.PENDING:
        return 'warning'
      case OrderStatus.CONFIRMED:
        return 'info'
      case OrderStatus.IN_PRODUCTION:
        return 'primary'
      case OrderStatus.SHIPPED:
        return 'success'
      case OrderStatus.DELIVERED:
        return 'success'
      case OrderStatus.CANCELLED:
        return 'error'
      default:
        return 'neutral'
    }
  }

  const getStatusText = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.PENDING:
        return 'Ausstehend'
      case OrderStatus.CONFIRMED:
        return 'Bestätigt'
      case OrderStatus.IN_PRODUCTION:
        return 'In Produktion'
      case OrderStatus.SHIPPED:
        return 'Versandt'
      case OrderStatus.DELIVERED:
        return 'Geliefert'
      case OrderStatus.CANCELLED:
        return 'Storniert'
      default:
        return status
    }
  }

  const handleViewOrder = (orderId: string) => {
    navigate(`/d/${driveId}/shop/orders/${orderId}`)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loading color="primary" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
          <h2 className="text-xl font-semibold text-error">{error}</h2>
          <Button
            color="primary"
            onClick={fetchOrders}
          >
            Erneut versuchen
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <Button
          color="primary"
          variant="outline"
          size="sm"
          onClick={() => navigate(`/d/${driveId}/shop`)}
        >
          <IconArrowBack size={20} />
          Zurück zum Shop
        </Button>

        <h1 className="text-2xl font-bold">
          <IconPackage size={28} className="inline mr-2" />
          Meine Bestellungen
        </h1>
      </div>

      {/* Orders List */}
      {orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
          <IconPackage size={80} className="text-gray-400" />
          <h2 className="text-xl font-semibold text-gray-600">Keine Bestellungen gefunden</h2>
          <p className="text-gray-500 text-center max-w-md">
            Sie haben noch keine Bestellungen aufgegeben. Entdecken Sie unsere Produkte im Shop.
          </p>
          <Button
            color="primary"
            size="lg"
            onClick={() => navigate(`/d/${driveId}/shop`)}
          >
            Zum Shop
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <Card key={order.id} className="bg-base-100 shadow-lg">
              <div className="card-body">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-semibold text-lg">
                      Bestellung #{order.order_number}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {formatDate(order.created_at)}
                    </p>
                  </div>
                  <Badge color={getStatusColor(order.status)}>
                    {getStatusText(order.status)}
                  </Badge>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <h4 className="font-medium mb-2">Versandadresse</h4>
                    <p className="text-sm text-gray-600">
                      {order.shipping_name}<br />
                      {order.shipping_address_line1}<br />
                      {order.shipping_address_line2 && (
                        <>{order.shipping_address_line2}<br /></>
                      )}
                      {order.shipping_postal_code} {order.shipping_city}<br />
                      {order.shipping_country}
                    </p>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">Bestellübersicht</h4>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span>Artikel:</span>
                        <span>{order.items.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Zwischensumme:</span>
                        <span>{formatPrice(order.subtotal)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Versand:</span>
                        <span>{formatPrice(order.shipping_amount)}</span>
                      </div>
                      <div className="flex justify-between font-semibold">
                        <span>Gesamt:</span>
                        <span>{formatPrice(order.total_amount)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {order.tracking_number && (
                  <div className="mb-4">
                    <h4 className="font-medium mb-1">Sendungsverfolgung</h4>
                    <p className="text-sm text-gray-600">
                      Tracking-Nummer: {order.tracking_number}
                    </p>
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <Button
                    color="primary"
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewOrder(order.id)}
                  >
                    <IconEye size={16} />
                    Details ansehen
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}